import React from "react";
import { Info, CircleCheck, TicketPercent, ShieldCheck, CalendarDays, Gauge } from "lucide-react";
import { eur } from "../lib/pricing";

function Row({ label, value, sub, strong, discount }) {
  let valueClass = "text-[#2a1a1e]";
  if (discount) {
    valueClass = "text-[#2e7d32] font-semibold";
  } else if (strong) {
    valueClass = "font-semibold text-[#2a1a1e]";
  }
  return (
    <div className="flex items-start justify-between gap-4 py-1.5">
      <div className="text-sm text-[#6d5a5e]">
        {label}
        {sub && <div className="text-xs text-[#9b8388]">{sub}</div>}
      </div>
      <div className={`text-sm tabular-nums ${valueClass}`}>{value}</div>
    </div>
  );
}

export default function BudgetSummary({ budget }) {
  if (!budget.ready) {
    return (
      <div className="rounded-2xl border border-[#efe4e6] bg-white p-6 text-center">
        <div className="mx-auto w-11 h-11 rounded-full bg-[#f8ecef] flex items-center justify-center mb-3">
          <Info className="w-5 h-5 text-[#631024]" />
        </div>
        <h3 className="font-display font-bold text-[#2a1a1e]">
          Tu presupuesto aparecerá aquí
        </h3>
        <p className="text-sm text-[#9b8388] mt-1.5 leading-relaxed">
          Elige un vehículo y las fechas de recogida y devolución.
        </p>
      </div>
    );
  }

  const { vehicle, insurance, days, vehicleCost, insuranceCost, extrasCost, discount, promo, total, fianza, kmIncluded } = budget;

  return (
    <div className="rounded-2xl border border-[#efe4e6] bg-white overflow-hidden animate-fade-up">
      <div className="bg-[#631024] text-white px-6 py-5">
        <p className="text-[11px] tracking-[0.18em] uppercase text-white/60">Resumen del presupuesto</p>
        <div className="flex items-center gap-2 mt-1">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-white/15 text-xs font-bold">
            {vehicle.group}
          </span>
          <h3 className="font-display font-bold text-lg">{vehicle.name}</h3>
        </div>
        <p className="text-sm text-white/70">{vehicle.type} · {vehicle.detail}</p>
      </div>

      <div className="px-6 py-4">
        <div className="flex items-center gap-4 text-sm text-[#6d5a5e] pb-3 border-b border-[#f0e7e9]">
          <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-[#631024]" /> {days} día{days > 1 ? "s" : ""}</span>
          <span className="inline-flex items-center gap-1.5"><Gauge className="w-4 h-4 text-[#631024]" /> {kmIncluded.toLocaleString("es-ES")} km incl.</span>
        </div>

        <div className="py-2 divide-y divide-[#f6eef0]">
          <Row label={`Alquiler vehículo`} sub={`${days} día${days > 1 ? "s" : ""} · tarifa aplicada`} value={eur(vehicleCost)} />
          <Row
            label={`Seguro ${insurance.name}`}
            sub={insuranceCost > 0 ? `${eur(insuranceCost / days)} / día` : "Incluido"}
            value={insuranceCost > 0 ? eur(insuranceCost) : "Incluido"}
          />
          {extrasCost > 0 && (
            <Row label="Entrega fuera de horario" value={eur(extrasCost)} />
          )}
          {discount > 0 && promo && (
            <Row label={promo.label} sub={promo.note} value={`− ${eur(discount)}`} discount />
          )}
        </div>

        <div className="flex items-end justify-between pt-3 border-t border-[#f0e7e9]">
          <div>
            <p className="text-sm text-[#6d5a5e]">Total estimado</p>
            <p className="text-[11px] text-[#9b8388]">IVA incluido · orientativo</p>
          </div>
          <p className="font-display font-800 text-3xl text-[#631024]" style={{ fontWeight: 800 }}>{eur(total)}</p>
        </div>

        <div className="mt-4 rounded-xl bg-[#f8ecef] px-4 py-3 flex items-start gap-2.5">
          <ShieldCheck className="w-4 h-4 text-[#631024] mt-0.5 shrink-0" />
          <div className="text-xs text-[#6d5a5e]">
            <span className="font-semibold text-[#2a1a1e]">Fianza a retener: {fianza > 0 ? eur(fianza) : "Sin fianza"}</span>
            <div>Bloqueo temporal en tarjeta según la cobertura contratada ({insurance.fianzaLabel}).</div>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2 text-xs text-[#9b8388]">
          <CircleCheck className="w-3.5 h-3.5 text-[#2e7d32]" />
          Sin pago ahora · confirmamos disponibilidad por email.
        </div>
      </div>
    </div>
  );
}
